if (!customElements.get('bestsellers-slider')) {
  const RENDERED_YOTPO_SELECTOR = '.yotpo-reviews-star-ratings-widget, .yotpo-sr-bottom-line-summary';

  class BestsellersSlider extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isSliderActive = false;
      this.visibleCount = 1;
      this.mobileNavAlignment = null;
      this.cardRenderCache = new Map();
      this.ratingHtmlCache = new Map();
      this.cardRequestControllers = new Map();
      this.prefetchAbortController = null;
    }

    connectedCallback() {
      this.track = this.querySelector('.bestsellers__grid');
      this.items = Array.from(this.querySelectorAll('.product-card'));
      if (!this.track || this.items.length === 0) return;

      this.buildControls();
      this.handleResize = this.onResize.bind(this);
      this.handleTouchStart = this.onTouchStart.bind(this);
      this.handleTouchMove = this.onTouchMove.bind(this);
      this.handleTouchEnd = this.onTouchEnd.bind(this);
      this.handleMouseDown = this.onMouseDown.bind(this);
      this.handleMouseMove = this.onMouseMove.bind(this);
      this.handleMouseUp = this.onMouseUp.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleSiblingSwatchClick = this.onSiblingSwatchClick.bind(this);
      this.prefetchAbortController = new AbortController();

      window.addEventListener('resize', this.handleResize);
      this.addEventListener('click', this.handleSiblingSwatchClick);
      this.onResize();
      this.initSiblingScrollers();
      this.primeRatingCache();
      this.warmCardRenderCache();
      this.bindImageAlignmentListeners();
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      this.removeEventListener('click', this.handleSiblingSwatchClick);
      this.unbindTouch();
      this.unbindMouse();
      this.cardRequestControllers.forEach((controller) => controller.abort());
      this.cardRequestControllers.clear();
      if (this.prefetchAbortController) {
        this.prefetchAbortController.abort();
        this.prefetchAbortController = null;
      }
    }

    buildControls() {
      this.nav = document.createElement('div');
      this.nav.classList.add('bestsellers-slider__nav');
      this.nav.innerHTML = `
        <button class="bestsellers-slider__btn bestsellers-slider__btn--prev" aria-label="Previous">
          <svg xmlns="http://www.w3.org/2000/svg" width="43" height="43" viewBox="0 0 43 43" fill="none">
            <path d="M41.679 21.089C41.679 32.46 32.461 41.678 21.09 41.678C9.718 41.678 0.5 32.46 0.5 21.089C0.5 9.71798 9.718 0.499977 21.09 0.499977C32.461 0.499977 41.679 9.71798 41.679 21.089Z" fill="white" stroke="#E98299"/>
            <path d="M25.59 11.85L15.5 21.94L25.238 31.678" stroke="#E98299"/>
          </svg>
        </button>
        <button class="bestsellers-slider__btn bestsellers-slider__btn--next" aria-label="Next">
          <svg xmlns="http://www.w3.org/2000/svg" width="43" height="43" viewBox="0 0 43 43" fill="none">
            <path d="M41.679 21.089C41.679 32.46 32.461 41.678 21.09 41.678C9.718 41.678 0.5 32.46 0.5 21.089C0.5 9.71798 9.718 0.499977 21.09 0.499977C32.461 0.499977 41.679 9.71798 41.679 21.089Z" fill="white" stroke="#E98299"/>
            <path d="M17.09 11.85L27.18 21.94L17.44 31.678" stroke="#E98299"/>
          </svg>
        </button>
      `;
      this.btnPrev = this.nav.querySelector('.bestsellers-slider__btn--prev');
      this.btnNext = this.nav.querySelector('.bestsellers-slider__btn--next');
      this.btnPrev.addEventListener('click', () => this.prev());
      this.btnNext.addEventListener('click', () => this.next());
    }

    getVisibleCount() {
      if (window.innerWidth >= 992) return 4;
      if (window.innerWidth >= 768) return 2;
      return 1;
    }

    activate() {
      this.isSliderActive = true;
      this.visibleCount = this.getVisibleCount();
      this.currentIndex = Math.min(this.currentIndex, this.maxIndex());

      this.track.classList.add('bestsellers__grid--slider');
      this.items.forEach((item) => item.classList.add('product-card--slide'));

      if (!this.nav.parentNode) {
        this.prepend(this.nav);
      }

      this.bindTouch();
      this.bindMouse();
      this.goTo(this.currentIndex, false);
      this.updateNavAlignment(true);
    }

    deactivate() {
      if (!this.isSliderActive) return;
      this.isSliderActive = false;

      this.track.classList.remove('bestsellers__grid--slider');
      this.items.forEach((item) => item.classList.remove('product-card--slide'));
      this.track.style.transform = '';
      this.track.style.transition = '';
      this.mobileNavAlignment = null;
      this.style.removeProperty('--bestsellers-nav-top');
      this.style.removeProperty('--bestsellers-nav-left');
      this.style.removeProperty('--bestsellers-nav-right');

      if (this.nav.parentNode) this.nav.parentNode.removeChild(this.nav);
      this.unbindTouch();
      this.unbindMouse();
    }

    onResize() {
      const newVisible = this.getVisibleCount();

      if (this.items.length <= newVisible) {
        this.deactivate();
        return;
      }

      const wasActive = this.isSliderActive;
      const prevVisible = this.visibleCount;

      this.visibleCount = newVisible;

      if (!wasActive) {
        this.currentIndex = 0;
        this.activate();
      } else {
        if (prevVisible !== newVisible) {
          this.currentIndex = Math.min(this.currentIndex, this.maxIndex());
        }
        this.mobileNavAlignment = null;
        this.goTo(this.currentIndex, false);
        this.updateNavAlignment(true);
      }
    }

    maxIndex() {
      return Math.max(0, this.items.length - this.visibleCount);
    }

    getSlideWidth() {
      if (this.items.length === 0) return 0;
      const item = this.items[0];
      const style = getComputedStyle(item);
      const marginRight = parseFloat(style.marginRight) || 0;
      return item.offsetWidth + marginRight;
    }

    goTo(index, animate = true) {
      this.currentIndex = Math.max(0, Math.min(index, this.maxIndex()));

      const slideWidth = this.getSlideWidth();
      const offset = -(this.currentIndex * slideWidth);

      this.track.style.transition = animate ? 'transform 0.35s ease' : 'none';
      this.track.style.transform = `translateX(${offset}px)`;

      this.updateButtons();
    }

    bindImageAlignmentListeners() {
      const images = this.querySelectorAll('.product-card__image-wrapper img, .product-card__image');
      images.forEach((image) => {
        if (image.complete) return;
        image.addEventListener('load', () => {
          this.mobileNavAlignment = null;
          this.updateNavAlignment(true);
        }, { once: true });
      });
    }

    updateNavAlignment(force = false) {
      if (!this.nav || !this.btnPrev || !this.btnNext || !this.isSliderActive) return;

      if (window.innerWidth > 767) {
        this.mobileNavAlignment = null;
        this.style.removeProperty('--bestsellers-nav-top');
        this.style.removeProperty('--bestsellers-nav-left');
        this.style.removeProperty('--bestsellers-nav-right');
        return;
      }

      if (this.mobileNavAlignment && !force) {
        this.style.setProperty('--bestsellers-nav-top', `${this.mobileNavAlignment.top}px`);
        this.style.setProperty('--bestsellers-nav-left', `${this.mobileNavAlignment.left}px`);
        this.style.setProperty('--bestsellers-nav-right', `${this.mobileNavAlignment.right}px`);
        return;
      }

      const referenceCard = this.items[0] || this.items[this.currentIndex];
      if (!referenceCard) return;

      const imageWrapper =
        referenceCard.querySelector('.product-card__image-wrapper') ||
        referenceCard.querySelector('.product-card__image');

      if (!imageWrapper) return;

      const sliderRect = this.getBoundingClientRect();
      const imageRect = imageWrapper.getBoundingClientRect();
      const buttonRect = this.btnPrev.getBoundingClientRect();

      if (!sliderRect.width || !imageRect.width) return;

      const buttonHalf = (buttonRect.width || 32) / 2;
      const slideOffset = this.currentIndex * this.getSlideWidth();
      const normalizedImageLeft = imageRect.left + slideOffset;
      const normalizedImageRight = normalizedImageLeft + imageRect.width;
      const top = imageRect.top - sliderRect.top + imageRect.height / 2;
      const viewportInset = window.innerWidth <= 380 ? 18 : 22;
      const left = viewportInset - sliderRect.left;
      const right = viewportInset - (window.innerWidth - sliderRect.right);

      this.mobileNavAlignment = {
        top: Math.round(top),
        left: Math.round(left),
        right: Math.round(right),
      };

      this.style.setProperty('--bestsellers-nav-top', `${this.mobileNavAlignment.top}px`);
      this.style.setProperty('--bestsellers-nav-left', `${this.mobileNavAlignment.left}px`);
      this.style.setProperty('--bestsellers-nav-right', `${this.mobileNavAlignment.right}px`);
    }

    next() {
      this.goTo(this.currentIndex + 1);
    }

    prev() {
      this.goTo(this.currentIndex - 1);
    }

    updateButtons() {
      this.btnPrev.disabled = this.currentIndex === 0;
      this.btnNext.disabled = this.currentIndex >= this.maxIndex();
    }

    /* Touch / swipe support */
    bindTouch() {
      this.track.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      this.track.addEventListener('touchmove', this.handleTouchMove, { passive: false });
      this.track.addEventListener('touchend', this.handleTouchEnd, { passive: true });
    }

    unbindTouch() {
      this.track.removeEventListener('touchstart', this.handleTouchStart);
      this.track.removeEventListener('touchmove', this.handleTouchMove);
      this.track.removeEventListener('touchend', this.handleTouchEnd);
    }

    onTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchDeltaX = 0;
      this.isSwiping = false;
    }

    onTouchMove(e) {
      this.touchDeltaX = e.touches[0].clientX - this.touchStartX;
      const deltaY = Math.abs(e.touches[0].clientY - this.touchStartY);

      if (!this.isSwiping && Math.abs(this.touchDeltaX) > deltaY && Math.abs(this.touchDeltaX) > 5) {
        this.isSwiping = true;
      }

      if (this.isSwiping) {
        e.preventDefault();
        const slideWidth = this.getSlideWidth();
        const base = -(this.currentIndex * slideWidth);
        this.track.style.transition = 'none';
        this.track.style.transform = `translateX(${base + this.touchDeltaX}px)`;
      }
    }

    onTouchEnd() {
      if (!this.isSwiping) return;
      const threshold = 50;
      if (this.touchDeltaX < -threshold) {
        this.next();
      } else if (this.touchDeltaX > threshold) {
        this.prev();
      } else {
        this.goTo(this.currentIndex);
      }
    }

    /* Mouse / drag support */
    bindMouse() {
      this.track.addEventListener('mousedown', this.handleMouseDown);
      this.track.addEventListener('click', this.handleClick, true);
      this.track.style.cursor = 'grab';
    }

    unbindMouse() {
      this.track.removeEventListener('mousedown', this.handleMouseDown);
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
      this.track.removeEventListener('click', this.handleClick, true);
      this.track.style.cursor = '';
      document.body.style.userSelect = '';
    }

    handleClick(e) {
      if (this.wasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    onMouseDown(e) {
      if (e.target.closest('a, button, input, select')) return;

      this.isDragging = true;
      this.wasDragged = false;
      this.mouseStartX = e.pageX;
      this.mouseDeltaX = 0;
      this.track.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';

      document.addEventListener('mousemove', this.handleMouseMove);
      document.addEventListener('mouseup', this.handleMouseUp);

      e.preventDefault();
    }

    onMouseMove(e) {
      if (!this.isDragging) return;

      this.mouseDeltaX = e.pageX - this.mouseStartX;

      if (Math.abs(this.mouseDeltaX) > 5) {
        this.wasDragged = true;
      }

      const slideWidth = this.getSlideWidth();
      const base = -(this.currentIndex * slideWidth);
      this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${base + this.mouseDeltaX}px)`;
    }

    onMouseUp() {
      if (!this.isDragging) return;

      this.isDragging = false;
      this.track.style.cursor = 'grab';
      document.body.style.userSelect = '';

      const threshold = 50;
      if (this.mouseDeltaX < -threshold) {
        this.next();
      } else if (this.mouseDeltaX > threshold) {
        this.prev();
      } else {
        this.goTo(this.currentIndex);
      }

      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);

      setTimeout(() => {
        this.wasDragged = false;
      }, 10);
    }

    initSiblingScrollers(root = this) {
      const siblingsElements = Array.from(root.querySelectorAll('[data-bestsellers-siblings]'));
      if (siblingsElements.length === 0) return;

      siblingsElements.forEach((siblingsElement) => this.initSiblingsScroll(siblingsElement));
    }

    warmCardRenderCache() {
      const scheduler = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 120));
      scheduler(() => {
        const handles = Array.from(
          new Set(
            Array.from(this.querySelectorAll('[data-sibling-product-handle]'))
              .map((node) => node.dataset.siblingProductHandle)
              .filter(Boolean)
          )
        );

        handles.slice(0, 16).forEach((handle) => {
          this.prefetchProductCard(handle);
        });
      });
    }

    async prefetchProductCard(productHandle) {
      const sourceSectionId = this.closest('.bestsellers')?.dataset.sectionId;
      const cacheKey = `${sourceSectionId || 'default'}::${productHandle}`;
      if (this.cardRenderCache.has(cacheKey)) return;

      const rootUrl = window.Shopify?.routes?.root || '/';
      const productSectionUrl = `${rootUrl}products/${encodeURIComponent(productHandle)}?section_id=bestsellers-card-render&source_section_id=${encodeURIComponent(sourceSectionId || '')}`;

      try {
        const sectionHtml = await this.fetchSectionHtml(productSectionUrl, this.prefetchAbortController?.signal);
        if (!sectionHtml || !sectionHtml.trim()) return;

        const renderedCardHtml = this.extractRenderedCardHtml(sectionHtml);
        if (!renderedCardHtml) return;

        this.cardRenderCache.set(cacheKey, renderedCardHtml);
        this.preloadPrimaryImage(renderedCardHtml);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        // Silent prefetch failure to avoid noisy logs for background warmup.
      }
    }

    preloadPrimaryImage(renderedCardHtml) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(renderedCardHtml, 'text/html');
      const primaryImage = doc.querySelector('.product-card__image--primary');
      const src = primaryImage?.getAttribute('src');
      if (!src) return;

      const img = new Image();
      img.src = src;
    }

    async onSiblingSwatchClick(event) {
      const swatchLink = event.target.closest('[data-sibling-product-handle]');
      if (!swatchLink || !this.contains(swatchLink)) return;

      event.preventDefault();

      const nextProductHandle = swatchLink.dataset.siblingProductHandle;
      const productCard = swatchLink.closest('.product-card');
      if (!nextProductHandle || !productCard) return;

      if (productCard.dataset.currentProductHandle === nextProductHandle) return;

      await this.renderProductCard(productCard, nextProductHandle);
    }

    async renderProductCard(productCard, productHandle) {
      if (!productHandle) return;

      const rootUrl = window.Shopify?.routes?.root || '/';
      const sourceSectionId = this.closest('.bestsellers')?.dataset.sectionId;
      const cacheKey = `${sourceSectionId || 'default'}::${productHandle}`;
      const cachedCardHtml = this.cardRenderCache.get(cacheKey);
      if (cachedCardHtml) {
        this.applyRenderedCard(productCard, cachedCardHtml, productHandle);
        return;
      }

      const productSectionUrl = `${rootUrl}products/${encodeURIComponent(productHandle)}?section_id=bestsellers-card-render&source_section_id=${encodeURIComponent(sourceSectionId || '')}`;
      const previousController = this.cardRequestControllers.get(productCard);
      if (previousController) previousController.abort();

      const requestController = new AbortController();
      this.cardRequestControllers.set(productCard, requestController);

      try {
        const sectionHtml = await this.fetchSectionHtml(productSectionUrl, requestController.signal);
        if (!sectionHtml || !sectionHtml.trim()) return;

        const renderedCardHtml = this.extractRenderedCardHtml(sectionHtml);
        if (!renderedCardHtml) return;

        if (this.cardRequestControllers.get(productCard) !== requestController) return;

        this.cardRenderCache.set(cacheKey, renderedCardHtml);
        this.applyRenderedCard(productCard, renderedCardHtml, productHandle);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('Bestsellers card render failed:', error);
      } finally {
        if (this.cardRequestControllers.get(productCard) === requestController) {
          this.cardRequestControllers.delete(productCard);
        }
      }
    }

    async fetchSectionHtml(productSectionUrl, signal) {
      const sectionIdResponse = await fetch(productSectionUrl, {
        method: 'GET',
        headers: {
          Accept: 'text/html',
        },
        signal,
      });

      if (sectionIdResponse.ok) {
        const sectionIdHtml = await sectionIdResponse.text();
        if (sectionIdHtml && sectionIdHtml.trim()) {
          return sectionIdHtml;
        }
      }

      return '';
    }

    extractRenderedCardHtml(sectionHtml) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');
      const renderedCard = doc.querySelector('[data-rendered-product-card]');
      if (renderedCard) {
        return renderedCard.innerHTML.trim();
      }

      const sectionWrapper = doc.querySelector('.shopify-section');

      if (sectionWrapper) {
        return sectionWrapper.innerHTML.trim();
      }

      return doc.body ? doc.body.innerHTML.trim() : '';
    }

    applyRenderedCard(productCard, renderedCardHtml, productHandle) {
      const previousProductHandle = productCard.dataset.currentProductHandle;
      this.cacheRenderedRating(productCard, previousProductHandle);

      const container = document.createElement('div');
      container.innerHTML = renderedCardHtml;

      const incomingRating = container.querySelector('.product-card__rating');
      const preservedRatingHtml =
        this.ratingHtmlCache.get(productHandle) || this.getRenderedRatingHtml(productCard);

      if (incomingRating && preservedRatingHtml) {
        incomingRating.outerHTML = preservedRatingHtml;
      }

      productCard.innerHTML = container.innerHTML;
      productCard.dataset.currentProductHandle = productHandle;

      this.initSiblingScrollers(productCard);
      this.cacheRenderedRating(productCard, productHandle);
      this.bindImageAlignmentListeners();
      this.mobileNavAlignment = null;
      this.updateNavAlignment(true);

      this.focusActiveSwatch(productCard);
    }

    primeRatingCache() {
      const seedCache = () => {
        this.items.forEach((card) => {
          this.cacheRenderedRating(card, card.dataset.currentProductHandle);
        });
      };

      seedCache();

      window.setTimeout(() => {
        seedCache();
      }, 1200);
    }

    getRenderedRatingHtml(productCard) {
      const ratingElement = productCard?.querySelector('.product-card__rating');
      if (!ratingElement) return '';

      const hasRenderedStars = !!ratingElement.querySelector(RENDERED_YOTPO_SELECTOR);

      if (!hasRenderedStars) return '';

      return ratingElement.outerHTML;
    }

    cacheRenderedRating(productCard, productHandle) {
      if (!productHandle) return;

      const ratingHtml = this.getRenderedRatingHtml(productCard);
      if (ratingHtml) {
        this.ratingHtmlCache.set(productHandle, ratingHtml);
      }
    }

    focusActiveSwatch(productCard) {
      const activeSwatch = productCard.querySelector('.product-card__swatch-link--active');
      if (activeSwatch) {
        activeSwatch.focus({ preventScroll: true });
      }
    }

    initSiblingsScroll(siblingsElement) {
      if (!siblingsElement || siblingsElement.dataset.sliderInitialized === 'true') return;

      siblingsElement.dataset.sliderInitialized = 'true';

      const viewport = siblingsElement.querySelector('.product-card__variants-swiper');
      const wrapper = siblingsElement.querySelector('.swiper-wrapper');
      const prevBtn = siblingsElement.querySelector('.product-card__variants-nav--prev');
      const nextBtn = siblingsElement.querySelector('.product-card__variants-nav--next');

      if (!viewport || !wrapper || !prevBtn || !nextBtn) return;

      const slides = Array.from(wrapper.children);
      if (slides.length <= 1) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }

      const getScrollStep = () => {
        const firstSlide = slides[0];
        if (!firstSlide) return 44;
        const firstSlideStyle = getComputedStyle(firstSlide);
        const marginRight = parseFloat(firstSlideStyle.marginRight) || 0;
        return firstSlide.offsetWidth + marginRight;
      };

      const updateButtons = () => {
        const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        prevBtn.disabled = viewport.scrollLeft <= 0;
        nextBtn.disabled = viewport.scrollLeft >= maxScroll - 1;
      };

      prevBtn.addEventListener('click', () => {
        viewport.scrollBy({ left: -getScrollStep() * 3, behavior: 'smooth' });
      });

      nextBtn.addEventListener('click', () => {
        viewport.scrollBy({ left: getScrollStep() * 3, behavior: 'smooth' });
      });

      viewport.addEventListener('scroll', updateButtons, { passive: true });
      updateButtons();
    }
  }

  customElements.define('bestsellers-slider', BestsellersSlider);
}
